//React Hooks 
import { useState, useContext } from "react";

//MUI Components and Icons
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

//Custom Components
import CommonAccordion from "../../custom-components/CommonAccordion";
import { ReactComponent as UserStoryIcon } from "../../../images/userStoryIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../images/SecurityDeleteIcon.svg";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  SecurityFlowDataContext,
  SecurityLoaderStateContext,
} from "../../main-pages/SecurityTestingPage";
import {
  addToaddReview,
  fmTothreatModelling,
  secRecommendations,
  securityPlan,
} from "../../../services/webServices";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";
import SessionExpired from "../../popup-components/SessionExpiredPopup";

//Document related libraries
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const DesignPhase = () => {

  //Navigation and Routing
  const location = useLocation();
  const navigate = useNavigate();

  //output values from state
  const funModFromState = location.state?.funcModData;
  const secReqFromState = location.state?.securityReqData;
  const archDesignFromState = location.state?.archDesignData;

  //state variables for input
  const [selectedInputs, setSelectedInputs] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState({});

  //error popup
  const [openErrorPopup, setOpenErrorPopup] = useState(false);

  //useContext
  const { setLoadingState } = useContext(LoadingModalContext);
  const { securityLoaderState, setSecurityLoaderState } = useContext(
    SecurityLoaderStateContext
  );

  //checkbox
  const handleChange = (event) => {
    const { value, checked } = event.target;
    setSelectedInputs((prev) =>
      checked ? [...prev, value] : prev.filter((item) => item !== value)
    );
  };

  //File Upload 
  const UploadBox = ({ text, onFileChange, onFileRemove, fileName }) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          backgroundColor: "#EAF4FF",
          borderRadius: "8px",
          border: "1px dashed #49A3FF",
          cursor: "pointer",
          mt: 1,
        }}
      >
        <input
          type="file"
          accept=".pdf,.docx,.txt"
          style={{ display: "none" }}
          id={`upload-${text}`}
          onChange={onFileChange}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserStoryIcon width={20} height={20} />
          <Box
            sx={{ display: "flex", flexDirection: "column", textAlign: "center" }}
          >
            <Typography
              sx={{
                color: "#000",
                cursor: "pointer",
                fontSize: "14px",
                "&:hover": { textDecoration: "underline" },
              }}
              component="label"
              htmlFor={`upload-${text}`}
            >
              {fileName || text}
            </Typography>
            {!fileName && (
              <Typography
                variant="caption"
                sx={{ color: "#666", fontSize: "12px" }}
              >
                Accepted formats: Pdf, Doc, Text
              </Typography>
            )}
          </Box>
        </Box>
        {fileName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "13px",
                minWidth: "60px",
                ml: 5,
              }}
              onClick={() => document.getElementById(`upload-${text}`).click()}
            >
              Change
            </Button>
            <IconButton color="error" onClick={onFileRemove} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  //Overall upload component 
  const options = [
    {
      label: "Functional Modules",
      value: "Functional Modules",
      uploadText: "Upload Functional Modules Doc",
    },
    {
      label: "Security Requirements",
      value: "Security Requirements",
      uploadText: "Upload Security Requirements Doc",
    },
    {
      label: "Architecture Design",
      value: "Architecture Design",
      uploadText: "Upload Architecture Design Doc",
    },
  ];

  const Details = (
    <Box sx={{ pl: 1, width: "100%" }}>
      <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
        Select Input Type
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
        Please upload all of the following documents to proceed.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {options.map((option) => (
          <Box key={option.value} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  value={option.value}
                  checked={selectedInputs.includes(option.value)}
                  onChange={handleChange}
                />
              }
              label={option.label}
            />
            {selectedInputs.includes(option.value) && (
              <>
                <UploadBox
                  text={option.uploadText}
                  onFileChange={(e) => handleFileChange(e, option.value)}
                  onFileRemove={() => handleFileRemove(option.value)}
                  fileName={uploadedFiles[option.value]?.name}
                />
              </>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );


  //Functions for file upload - delete
  const handleFileChange = (event, value) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const allowedExtensions = [".pdf", ".docx", ".txt"];
    const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
    const getFileType = (name) => {
      const ext = name.split(".").pop();
      return ext ? ext.toUpperCase() : "FILE";
    };
    if (!isAllowed) {
      alert(`Unsupported file type. Please upload a ${allowedExtensions.join(", ")} file.`);
      event.target.value = null;
      setUploadedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[value];
        return newFiles;
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      let textContent = "";
      if (fileName.endsWith(".txt")) {
        textContent = e.target.result.trim();
      }
      else if (fileName.endsWith(".pdf")) {
        const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(e.target.result) }).promise;
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          textContent += content.items.map(item => item.str).join(" ");
        }
      }
      else if (fileName.endsWith(".docx")) {
        const { value } = await mammoth.extractRawText({ arrayBuffer: e.target.result });
        textContent = value.trim();
      }
      if (!textContent || textContent.length === 0) {
        alert(`Uploaded ${getFileType(fileName)} file is empty`);
        event.target.value = null;
        setUploadedFiles((prev) => {
          const newFiles = { ...prev };
          delete newFiles[value];
          return newFiles;
        });
        return;
      }
      setUploadedFiles((prev) => ({
        ...prev,
        [value]: { file, name: file.name },
      }));

      event.target.value = null;
    };

    if (fileName.endsWith(".txt")) reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
  };


  //Functions for file upload - change, delete
  const handleFileRemove = (value) => {
    setUploadedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[value];
      return newFiles;
    });
  };


  //handle design phase
  const handleCustomDesignPhase = async ({
    uploadedFuncModFile,
    uploadedSecReqFile,
    uploadedArchFile,
  }) => {
    let progressHandler;
    progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 73000 });
    progressHandler.start("Generating Security Recommendations");
    try {
      setSecurityLoaderState("Design Phase");
      const parseDocumentAPI = async (file) => {
        const extension = file.name.split(".").pop().toLowerCase();
        if (extension === "txt") {
          return await file.text();
        }
        if (extension === "docx") {
          const arrayBuffer = await file.arrayBuffer();
          const { value } = await mammoth.extractRawText({ arrayBuffer });
          return value;
        }
        if (extension === "pdf") {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          let text = "";
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const pageText = content.items.map((item) => item.str).join(" ");
            text += pageText + "\n";
          }
          return text;
        }
        throw new Error("Unsupported file format");
      };

      const funcModData = uploadedFuncModFile
        ? await parseDocumentAPI(uploadedFuncModFile.file)
        : funModFromState;
      console.log(funcModData);

      const secReqData = uploadedSecReqFile
        ? await parseDocumentAPI(uploadedSecReqFile.file)
        : secReqFromState;

      const archDesignData = uploadedArchFile
        ? await parseDocumentAPI(uploadedArchFile.file)
        : archDesignFromState;
      console.log(archDesignData);

      const secRecommendationsResponse = await secRecommendations({
        fmInput: funcModData,
        secReq: secReqData,
      });
      const securityRecommendations = secRecommendationsResponse.data;

      progressHandler.updateMessage("Generating Threat Modelling");

      const threatModellingResponse = await fmTothreatModelling({
        funcMod: funcModData,
        securityReq: secReqData,
        securityRecom: securityRecommendations,
        archDesign: archDesignData,
      });
      const threatModelling = threatModellingResponse.data;

      progressHandler.updateMessage("Generating Architecture Design review");

      const addReviewResponse = await addToaddReview({
        add: archDesignData,
      });
      const addReview = addReviewResponse.data;

      await progressHandler.finishAndClose();

      navigate("/dashboard/security-testing/design-phase-output", {
        state: {
          securityRecommendations,
          threatModelling,
          addReview,
          //securityTestPlan,
          archDesign: archDesignData,
        },
      });
    } catch (error) {
      console.error("Error during design phase:", error);
      if (error.response.data.message === "Your session has expired.") {
        setOpenErrorPopup(true);
      }
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
      setSecurityLoaderState(null);
    }
  };

  return (
    <>
      <CommonAccordion
        summary={
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
              Design Phase
            </Typography>
          </Box>
        }
        details={Details}
        buttons={[
          {
            label: "Generate",
            variant: "contained",
            startIcon: <SendIcon />,
            sx: { float: "right", textTransform: "none" },
            disabled: !(
              uploadedFiles["Functional Modules"] &&
              uploadedFiles["Security Requirements"] &&
              uploadedFiles["Architecture Design"]
            ),
            onClick: () =>
              handleCustomDesignPhase({
                uploadedFuncModFile: uploadedFiles["Functional Modules"],
                uploadedSecReqFile: uploadedFiles["Security Requirements"],
                uploadedArchFile: uploadedFiles["Architecture Design"],
              }),
          },
        ]}
        defaultExpanded={true}
        sx={{
          width: "60%",
          maxWidth: "600px",
        }}
      />
      {openErrorPopup && <SessionExpired />}
    </>
  );
};

export default DesignPhase;
